
import { useEffect, useState } from 'react';

interface HeroProps {
  onRegister: () => void;
  onBookSession: () => void;
}

export default function Hero({ onRegister, onBookSession }: HeroProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const targetDate = new Date('2026-02-11T09:00:00').getTime();
    
    const updateCountdown = () => {
      const now = new Date().getTime();
      const difference = targetDate - now;
      
      if (difference > 0) {
        setCountdown({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[75vh] flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20Dubai%20skyline%20at%20twilight%20with%20futuristic%20glass%20buildings%20and%20palm%20trees%20silhouettes%20against%20a%20gradient%20sky%20of%20deep%20navy%20blue%20and%20warm%20amber%20tones%20professional%20corporate%20atmosphere%20clean%20architectural%20photography&width=1920&height=1080&seq=hero-bg-navy-1&orientation=landscape"
          alt="Dubai Skyline"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
      </div>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className={`text-2xl font-bold tracking-tight ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
            COVASANT
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#solutions" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>Solutions</a>
            <a href="#leaders" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>Our Team</a>
            <a href="#faq" className={`text-sm font-medium transition-colors ${isScrolled ? 'text-gray-600 hover:text-gray-900' : 'text-white/80 hover:text-white'}`}>FAQ</a>
            <button
              onClick={onRegister}
              className={`px-5 py-2 text-sm font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer ${isScrolled ? 'bg-[#1e3a8a] text-white hover:bg-[#1e40af]' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
            >
              Register Now
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-16">
        {/* Event Badge */}
        <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-5 py-2 mb-6">
          <span className="w-2 h-2 bg-[#3b82f6] rounded-full animate-pulse"></span>
          <span className="text-white/90 text-sm font-medium">IDC Middle East CIO Summit 2026 · Booth TFG 28</span>
        </div>

        {/* Main Headline */}
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
          From AI Pilot to<br />
          <span className="text-[#3b82f6]">Enterprise-Scale Production</span>
        </h1>

        {/* Subheadline */}
        <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-6 leading-relaxed">
          Meet Covasant at Atlantis, The Palm · Dubai · 11–12 February 2026. Discover how we help enterprises operationalize Agentic AI with governance, measurable ROI, and production-grade architecture.
        </p>

        {/* Countdown Timer */}
        <div className="flex items-center justify-center gap-3 md:gap-4 mb-8">
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 min-w-[70px]">
            <div className="text-2xl md:text-3xl font-bold text-white">{countdown.days}</div>
            <div className="text-xs text-white/60 uppercase tracking-wide">Days</div>
          </div>
          <div className="text-white/40 text-2xl font-light">:</div>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 min-w-[70px]">
            <div className="text-2xl md:text-3xl font-bold text-white">{countdown.hours.toString().padStart(2, '0')}</div>
            <div className="text-xs text-white/60 uppercase tracking-wide">Hours</div>
          </div>
          <div className="text-white/40 text-2xl font-light">:</div>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 min-w-[70px]">
            <div className="text-2xl md:text-3xl font-bold text-white">{countdown.minutes.toString().padStart(2, '0')}</div>
            <div className="text-xs text-white/60 uppercase tracking-wide">Mins</div>
          </div>
          <div className="text-white/40 text-2xl font-light">:</div>
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-3 min-w-[70px]">
            <div className="text-2xl md:text-3xl font-bold text-white">{countdown.seconds.toString().padStart(2, '0')}</div>
            <div className="text-xs text-white/60 uppercase tracking-wide">Secs</div>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <button
            onClick={onRegister}
            className="w-full sm:w-auto px-8 py-3.5 bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg shadow-[#1e3a8a]/30 whitespace-nowrap cursor-pointer"
          >
            Register & Meet Covasant
          </button>
          <button
            onClick={onBookSession}
            className="w-full sm:w-auto px-8 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
          >
            Book 30-Min Strategy Session
          </button>
        </div>

        {/* Event Details */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-white/60 text-sm">
          <div className="flex items-center gap-2">
            <i className="ri-calendar-line text-lg"></i>
            <span>11–12 February 2026</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-map-pin-line text-lg"></i>
            <span>Atlantis, The Palm · Dubai</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-group-line text-lg"></i>
            <span>500+ CIOs & Digital Leaders</span>
          </div>
        </div>
      </div>
    </section>
  );
}
